import { useState, useEffect, useRef } from 'react'
import cn from 'classnames';
import { isBefore, isAfter, intervalToDuration, addDays } from 'date-fns';
import * as Promise from "bluebird";
import keys from 'lodash.keys'
import debounce from 'lodash.debounce'
import dayjs from 'dayjs'

import { postOrder, windowRedirect, fetchSkuAvsCount, eventLog } from './api'
import { formatDateRange } from './utils';

import AmountToggle from './AmountToggle';
import Calendar from './Calendar';
import CloseIcon from './Icons/CloseIcon';
import EditIcon from './Icons/EditIcon';
import CartIcon from './Icons/CartIcon';

import './CheckoutPopup.css'

import { API_HOST } from './utils'
import { filterAvailable, filterUnavailable } from './availability'
import {
  getStockByDates,
  mergeStocksByDates,
  getDurationOptions,
  adjustStockForDuration,
} from './availability'
import useOnClickOutside from './hooks';


const CheckoutPopup = (props) => {
  const { hidePopup, checkCount, totalPrice, dateRange={}, cartData, checkDateRange,
    rentalData, setCartData, isDesktop, productData, isPickUp
  } = props;

  const [ err, setErr ] = useState(null)
  const [ isAvailable, setIsAvailable ] = useState(null)
  const [ showCalendar, setShowCalendar ] = useState(false)
  const [ skuAvsCount, setSkuAvsCount ] = useState({})
  const [ isLoading, setIsLoading ] = useState(false)

  const calendarRef = useRef();

  useOnClickOutside(calendarRef, () => setShowCalendar(false));

  useEffect(() => {
    setErr(null)
    getSkuAvailabilitiesCount()
    checkIsOK()
  }, [cartData]);

  const getSkuAvailabilitiesCount = async() => {
    const skuAvsTotalCount =  await Promise.reduce(cartData.skus, async(skuCount, curSku) => {
      const skuTotalCount = await fetchSkuAvsCount(curSku.strId);
      return { ...skuCount, [curSku.strId]: skuTotalCount }
    }, {});
    setSkuAvsCount(skuAvsTotalCount)
  }

  const { dateFrom, dateTo, sessionId } = rentalData
  let formattedRange = dateRange
    ? formatDateRange(dateRange.dateFrom, dateRange.dateTo)
    : 'Select a rental period'

  const interval = intervalToDuration({ start: new Date(dateFrom), end: new Date(dateTo) })
  const rentalInterval = interval.days + 1;

  const getFormattedProducts = () => {
    return cartData.products.map(item => {
      const currentSku = cartData.skus.find(sku => sku.strId === item.skuId) || {};
      const { color, pricing, size } = currentSku
      return {
        ...item,
        color,
        pricing,
        size
      }
    });
  }

  const handleChangeDate = (dates) => {
    const { dateFrom, dateTo } = dates;
    const newData = {
      ...rentalData,
      dateFrom,
      dateTo,
    }
    const jsonRentalData = JSON.stringify(newData)
    localStorage.setItem('rentalData', jsonRentalData)
    setCartData()
    checkDateRange()
  }

  const deleteProduct = (product) => {
    const { strId, skuId } = product;
    const currentProductIndex = cartData.products.findIndex(item => item.strId === strId && item.skuId === skuId);
    const newProducts = [ ...cartData.products ]
    newProducts.splice(currentProductIndex, 1)

    if(newProducts.length === 0) {
      localStorage.removeItem('rentalData')
      hidePopup()
    } else {
      const productsWithCurrentSku = newProducts.filter(item => item.skuId === skuId)
      const currentSkuIndex = cartData.skus.findIndex(item => item.strId === skuId)

      const newSkus = [ ...cartData.skus ]
      if(!productsWithCurrentSku.length) {
        newSkus.splice(currentSkuIndex, 1)
      }

      const newData = {
        products: newProducts,
        dateFrom: rentalData.dateFrom,
        dateTo: rentalData.dateTo,
        skus: newSkus
      }

      if(rentalData.isSameDayDelivery) {
        newData.isSameDayDelivery = rentalData.isSameDayDelivery
      }

      const jsonRentalData = JSON.stringify(newData)
      localStorage.setItem('rentalData', jsonRentalData)
    }

    eventLog({event_id: 5, product_sku: product.skuId})
    setCartData()
    checkCount()
  }

  const handleChangeAmount = (product, action) => {
    if(action === 'down' && product.amount === 1) {
      return;
    }
    
    const newProducts = [ ...cartData.products ]
    const currentProductId = newProducts.findIndex(item => item.strId === product.strId && item.skuId === product.skuId);
    let newProductAmount = action === 'up' ? product.amount + 1 : product.amount - 1
    if(newProductAmount < 0) {
      newProductAmount = 0
    }
    newProducts.splice(currentProductId, 1, { ...product, amount: newProductAmount })
    const newData = {
      ...rentalData,
      products: newProducts,
    }
    const jsonRentalData = JSON.stringify(newData)
    localStorage.setItem('rentalData', jsonRentalData)
    setCartData()
    checkCount()
  }

  const getProductsStatuses = (product) => {
    const avsForCurrentProduct = cartData.skuAvs.filter(item => item.skuStrId === product.skuId)

    const avsOutCurrentDateRange = avsForCurrentProduct
    .filter(item => item.dateTo && isAfter(new Date(dateFrom), new Date(item.dateTo)) ||
        isBefore(new Date(dateTo), new Date(item.dateFrom)))
    const avsOutCurrentDateRangeIds = avsOutCurrentDateRange.map(item => item.strId)

    const avsForCurrentDateRange = avsForCurrentProduct.filter(item => !avsOutCurrentDateRangeIds.includes(item.strId))

    if(isAfter(new Date(dateFrom), addDays(new Date(), 4)) && isAfter(new Date(dateTo), addDays(new Date(), rentalInterval))) {
      return avsForCurrentDateRange

    } else {
      return []
    }

  }

  const getCounts = (product) => {
    const statuses = getProductsStatuses(product)
    const availableStatuses = statuses.filter(filterAvailable)
    const unAvailableStatuses = statuses.filter(filterUnavailable);

    const unAvailableCount = unAvailableStatuses
      ? unAvailableStatuses.reduce((acc, cur) => {
          return acc + cur.amount
        }, 0)
      : 0;

    const availableCount = availableStatuses
      ? availableStatuses.reduce((acc, cur) => {
          return acc + cur.amount
        }, 0)
      : 0;

    return {
      availableCount,
      unAvailableCount
    }
  }

  const renderStatuses = (product) => {
    const { availableCount, unAvailableCount } = getCounts(product)

    const availableStatus = availableCount > unAvailableCount && availableCount !== 0 && product.amount <= availableCount;

    const statusText = availableStatus
      ? 'available'
      : availableCount
        ? 'available'
        : 'unavailable';

    const statusCount = availableStatus
      ? availableCount - unAvailableCount
      : availableCount || ''

    const statusClasses = cn('rntr-checkout__status', {
      'rntr-checkout__status_available': availableStatus,
      'rntr-checkout__status_unavailable': !availableStatus
    })

    return (
      <div className={ statusClasses }>
        <span className='rntr-checkout__point'/>{ statusCount } { statusText }
      </div>
    )
  }

  const getPrice = (product) => {
    const { skuId, amount } = product;
    const currentSku = cartData.skus.find(item => item.strId === skuId);
    let price = 0;
    if(currentSku) {
      const { pricing } = currentSku;
      const currentPrice = pricing ? pricing.find(item => Number(item.value) === rentalInterval) : null
      price = currentPrice ? `${ currentPrice.price * amount }` : 0
    }
    return price
  }


  const checkIsOK = debounce(() => {
    const productInfo = []
    for (const item of  getFormattedProducts()) {
      productInfo.push({ 'count' : item.amount, 'id' : item.skuId, 'free': false })
    }

    async function checkProduct(products) {
      for (const item of  products) {
        const resp = await fetchSkuAvsCount(item.id, dateRange)
        item.count > resp ? item.free = false : item.free = true
      }
      setIsAvailable(productInfo.every(product => {
        return product.free
      }))
    }
    checkProduct(productInfo)
  }, 1000)

  const onDateClick = () => {
    setShowCalendar(!showCalendar)
    eventLog({event_id: 7})
  }

  const checkItemIsOK = (item) => {
    const relevantAvs = productData.skuAvs.filter(avs => item.skuId === avs.skuStrId);
    const disabledDates = getStockByDates(relevantAvs)
    const { dateFrom, dateTo } = dateRange;
    const disabledDatesKeys = keys(disabledDates).sort()
    const lastDisabledDate = disabledDatesKeys[disabledDatesKeys.length - 1]

    if(!skuAvsCount) {
      return false;
    }

    if(isAfter(new Date(dateFrom), addDays(new Date(lastDisabledDate), 1))) {
      return true;
    }

    let date = dayjs(dateFrom)
    let dateStr = dayjs(dateFrom).format('YYYY-MM-DD')
    let isNotOk = false

    while (dateStr <= dateTo) {
      if(dateStr <= lastDisabledDate && disabledDates[dateStr] < 1) {
        isNotOk = true;
        break;
      }

      if(dateStr > lastDisabledDate && skuAvsCount > 0) {
        break
      }

      date = date.add(1, 'day')
      dateStr = date.format('YYYY-MM-DD')
    }

    return !isNotOk
  }

  const onCheckout = async () => {
    // console.log('onCheckout@data', cartData)
    if (!isAvailable) {
      return;
    }

    if (!cartData || !cartData.products || cartData.products.length === 0) {
      return;
    }
    setIsLoading(true)

    const skuList = getFormattedProducts()
      .map(item => ({ amount: item.amount, sku: item.skuId }))

    try {
      const orderData = {
        skuList,
        dateFrom,
        dateTo,
        sessionId,
      }
      if(rentalData.isSameDayDelivery) {
        orderData.deliveryType = isPickUp ? 'standard-shipping' : 'same-day'
      }
      const result = await postOrder(orderData)
       const { order } = result

      windowRedirect(`/checkout/${order.strId}`,  () => setIsLoading(false))
    } catch(e) {
      setIsLoading(false)
      setErr(e.toString())
    }
  }

  if(!cartData) return null

  const cartButtonClasses = cn('rntr-checkout__checkout-button', {
    'rntr-checkout__checkout-button_loading': isLoading
  })

  const datesButtonClasses = cn('rntr-checkout__dates', 'rntr-popover__button', {
    'rntr-checkout__dates_active': showCalendar
  })

  const editIconFill = showCalendar ? '#fff' : '#000'

  return (
    <div className='rntr-popup'>
      <div className='rntr-checkout__holder'>
        <button className='rntr-checkout__remove rntr-popover__button' onClick={ hidePopup } type='button'>
          <CloseIcon/>
        </button>
        <div className='rntr-header'>
          <div className='rntr-checkout__title'>MY ORDER</div>
          <button className={datesButtonClasses} onClick={() => { onDateClick(); eventLog({event_id: 10}); }} type="button">{ formattedRange }
            <span className='rntr-checkout__dates-icon'>
              <EditIcon fill={ editIconFill }/>
            </span>
          </button>
          { rentalData.isSameDayDelivery && <div className='rntr-checkout__delivery'>
            { isPickUp ? 'PICKUP IN STORE' : 'Same Day Delivery' }
            </div>}
        </div>
        <div className='rntr-checkout__list'>
          <div className='rntr-checkout__list-holder'>
            {
              getFormattedProducts().map(item => {
              const isItemOk = checkItemIsOK(item);
              const itemClasses = cn('rntr-item', {
                'rntr-item_error': !isItemOk
              })

              return (
                <div className={ itemClasses } key={item.strId} >
                  <button
                    type='button'
                    className='rntr-item__remove rntr-popover__button'
                    onClick={ () => deleteProduct(item) }>
                    <CloseIcon width='11' height='11' fill='#000'/>
                  </button>
                  <div className='rntr-card'>
                    <div className='rntr-imgWrapper'>
                      { item.images.length > 0 &&
                        <img className='rntr-img' src={`${API_HOST}/api/media/${item.images[0]}`}/>
                      }
                    </div>
                    <div className='rntr-checkout__info'>
                      <div className='rntr-checkout__info-top'>
                        <div className='rntr-checkout__info-title'>{ item.title }</div>
                        {/* <div className='rntr-checkout__info-title rntr-checkout__info-title_sku'>{ item.size } { item.color } </div>  */}
                        <div className='rntr-checkout__info-price'>${ getPrice(item) }</div>
                      </div>
                      <div className='rntr-checkout__info-title rntr-checkout__info-title_sku rntr-checkout__info-title_mobile'>{ item.size } { item.color } </div>
                      <AmountToggle
                        isDesktop={ isDesktop }
                        item={ item }
                        onClick={ handleChangeAmount }
                        counts={ getCounts(item) }/>
                      <div className='rntr-checkout__info-bottom'>
                        {/* <button className={datesButtonClasses} onClick={onDateClick}>{ formattedRange }
                          <span className='rntr-checkout__dates-icon'>
                            <EditIcon fill={ editIconFill }/>
                          </span>
                        </button>       */}
                        {/* <span className='rntr-checkout__cart-button rntr-popover__button'>
                          <CartIcon stroke={ isDesktop ? '#000' : '#398B82'}/>
                        </span>             */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            )}
          </div>

          </div>
        { showCalendar &&
          <div className='rntr-checkout__calendar'>
            <Calendar
              isActiveDayDelivery={ rentalData.isSameDayDelivery }
              cartData={ cartData }
              productData={ productData }
              dateRange={ [new Date(dateRange.dateFrom), new Date(dateRange.dateTo) ] }
              onChangeDate={ handleChangeDate }
              rentalDuration={ rentalInterval }
              show='dates'
              calendarRef={ calendarRef }/>
          </div>
        }
        <div className='rntr-checkout__bottom'>
          <div className='rntr-checkout__total'>
            Subtotal
          </div>
          <div className='rntr-checkout__price'>
            ${ rentalData.isSameDayDelivery && !isPickUp ? totalPrice + 25 : totalPrice }
          </div>
          {
            isAvailable === false && <p className='rntr-checkout-old__error'>
              Not all products are available during the selected period
            </p>
          }
          <a onClick={onCheckout} className={cartButtonClasses}>
            {isLoading ?
              <svg class="spinner" viewBox="0 0 50 50">
                <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
              </svg> :
              'checkout'
            }
          </a>
          {
            err && <p className="rntr-checkout_api-error" >{err}</p>
          }
        </div>
      </div>
    </div>
  )
}

export default CheckoutPopup

